import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NavBar from './NavBar';
import Footer from './Footer';

const SECTIONS = [
  {
    id: 'no-financial-advice',
    title: 'No Financial or Investment Advice',
    body: `Nothing on this website or in any materials produced by Quanturama constitutes financial advice, investment advice, trading advice, or any other form of regulated financial guidance. Any references to trading systems, bots, signals, or market activity are for informational and educational purposes only.

You should not make any financial or investment decisions based solely on information found on this website. Always consult a qualified financial advisor before making investment decisions.`
  },
  {
    id: 'no-guarantee-of-results',
    title: 'No Guarantee of Results',
    body: `Past performance of any trading system, bot, strategy, or service described on this website is not indicative of future results. All trading and investment activity carries significant risk, including the possible loss of all capital invested.

Quanturama makes no guarantees, representations, or warranties of any kind, express or implied, about the accuracy, completeness, suitability, or availability of any information, system, or service described on this website.`
  },
  {
    id: 'technology-disclaimer',
    title: 'Technology and Software Disclaimer',
    body: `Any software, systems, or tools referenced or delivered by Quanturama are provided "as is" without warranty of any kind. We do not guarantee uninterrupted operation, error-free performance, or compatibility with any specific environment.

Users are responsible for independently testing and validating any system before use in a live or production environment.`
  },
  {
    id: 'web3-cryptocurrency-risk',
    title: 'Web3 and Cryptocurrency Risk',
    body: `Cryptocurrency markets are highly volatile and unregulated in many jurisdictions. Smart contracts, decentralized applications, and blockchain-based systems carry inherent risks including smart contract bugs, network failures, and regulatory changes.

Quanturama is not responsible for any loss of funds arising from the use of any blockchain-based system, product, or protocol — whether developed by us or otherwise referenced on this website.`
  },
  {
    id: 'third-party-content',
    title: 'Third-Party Content and Links',
    body: `This website may contain links to third-party websites or reference third-party products and services. We do not endorse and are not responsible for the content, accuracy, or practices of any third-party site.`
  },
  {
    id: 'limitation-of-liability',
    title: 'Limitation of Liability',
    body: `To the fullest extent permitted by applicable law, Quanturama and its principals shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from your use of this website or any service described on it — including but not limited to financial losses, loss of data, or business interruption.`
  },
  {
    id: 'changes-to-disclaimer',
    title: 'Changes to This Disclaimer',
    body: `We may update this Disclaimer at any time. Changes will be posted on this page with an updated effective date. Continued use of this website constitutes acceptance of the updated Disclaimer.`
  },
  {
    id: 'contact',
    title: 'Contact',
    body: `If you have questions about this Disclaimer, please contact us at hello@quanturama.com.`
  }
];

const Disclaimer = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Disclaimer | Quanturama</title>
        <meta name="description" content="Quanturama Disclaimer — no financial advice, no guarantee of results. Read before using our services." />
        <link rel="canonical" href="https://quanturama.com/disclaimer" />
      </Helmet>

      <NavBar />

      <main style={{ paddingTop: '6rem', paddingBottom: '8rem', minHeight: '100vh' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1.5rem' }}>

          {/* Breadcrumb */}
          <Link
            to="/"
            style={{
              fontSize: '11px',
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              opacity: 0.35,
              textDecoration: 'none',
              color: 'inherit',
              display: 'inline-block',
              marginBottom: '2.5rem',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={e => e.target.style.opacity = 0.7}
            onMouseLeave={e => e.target.style.opacity = 0.35}
          >
            ← Back to Quanturama
          </Link>

          {/* Header */}
          <div style={{
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            paddingBottom: '2.5rem',
            marginBottom: '3rem'
          }}>
            <p style={{
              fontSize: '10px',
              letterSpacing: '0.4em',
              textTransform: 'uppercase',
              opacity: 0.3,
              marginBottom: '1rem'
            }}>
              Legal Document
            </p>
            <h1 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: 700,
              lineHeight: 1.15,
              marginBottom: '1rem',
              letterSpacing: '-0.02em'
            }}>
              Disclaimer
            </h1>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', opacity: 0.35 }}>Effective date: 1 January 2025</span>
              <span style={{ fontSize: '12px', opacity: 0.35 }}>Last updated: March 2026</span>
            </div>
          </div>

          {/* Intro */}
          <p style={{
            fontSize: '1rem',
            lineHeight: 1.85,
            opacity: 0.65,
            marginBottom: '3.5rem',
            borderLeft: '2px solid rgba(255,255,255,0.12)',
            paddingLeft: '1.25rem'
          }}>
            Please read this Disclaimer carefully before using quanturama.com or engaging with any product
            or service referenced on this website.
          </p>

          {/* Table of Contents */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: '8px',
            padding: '1.5rem 2rem',
            marginBottom: '4rem'
          }}>
            <p style={{
              fontSize: '10px',
              letterSpacing: '0.35em',
              textTransform: 'uppercase',
              opacity: 0.35,
              marginBottom: '1rem'
            }}>
              Contents
            </p>
            <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
              {SECTIONS.map((s, i) => (
                <li key={s.id} style={{ marginBottom: '0.4rem' }}>
                  <a
                    href={`#${s.id}`}
                    style={{
                      fontSize: '0.85rem',
                      opacity: 0.5,
                      textDecoration: 'none',
                      color: 'inherit',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={e => e.target.style.opacity = 1}
                    onMouseLeave={e => e.target.style.opacity = 0.5}
                  >
                    {i + 1}. {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* Sections */}
          {SECTIONS.map(({ id, title, body }, i) => (
            <div
              key={id}
              id={id}
              style={{
                marginBottom: '3rem',
                paddingBottom: '3rem',
                borderBottom: i < SECTIONS.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '10px', opacity: 0.25, fontVariantNumeric: 'tabular-nums', minWidth: '1.5rem' }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h2 style={{
                  fontSize: '0.78rem',
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                  opacity: 0.55,
                  fontWeight: 600
                }}>
                  {title}
                </h2>
              </div>
              {body.split('\n\n').map((para, j) => (
                <p key={j} style={{
                  fontSize: '0.92rem',
                  lineHeight: 1.9,
                  opacity: 0.65,
                  marginBottom: '0.9rem',
                  paddingLeft: '2.25rem'
                }}>
                  {para}
                </p>
              ))}
            </div>
          ))}

        </div>
      </main>

      <Footer />
    </>
  );
};

export default Disclaimer;
